/**
 * Who is calling (docs/AUTH.md). Two ways to be signed in:
 * - **session**: Google or a 6-digit email code gives our own token (`cc_…`), kept in localStorage and sent as
 *   `Authorization: Bearer`. A 401 on any call clears it and shows "Your sign-in expired. Please sign in again."
 *   Sign out calls POST /auth/logout, then clears it.
 * - **dev** (AUTH_MODE=dev, localhost or the demo only): pick who you are; the email is sent as `x-dev-user`
 *   (docs/ARCHITECTURE.md G7). Kept per browser tab (sessionStorage), so an "owner" tab and a "Sam" tab can run side by side.
 * `mode` decides which sign-in screen comes first; in dev mode, email sign-in is still reachable.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { config, IS_MOCK, type AuthMode } from "./env.ts";
import { AUTH_MESSAGES, createHttpAuth, type AuthClient, type SessionGrant } from "./lib/authClient.ts";
import { createMockAuth } from "./mock/api.ts";
import { sharedMockStore } from "./mock/store.ts";
import { clearDrafts } from "./lib/draft.ts";
import { clearWalkPhotosOf } from "./lib/walkPhotoDb.ts";

export interface AuthUser { uid: string; email: string; name: string }

export interface AuthState {
  mode: AuthMode;
  status: "loading" | "signedOut" | "signedIn";
  /** How the current person signed in. */
  kind?: "dev" | "session";
  user?: AuthUser;
  error?: string;
  /** A plain message for the sign-in screen, e.g. after a session expired. */
  notice?: string;
  /** The /auth routes (HTTP, or the in-browser demo). */
  client: AuthClient;
  /** Headers that prove who is calling (Authorization: Bearer, or x-dev-user). */
  headers(): Promise<Record<string, string>>;
  signInDev(email: string, name?: string): void;
  signInWithSession(grant: SessionGrant): void;
  /** The server said 401: forget the token and ask to sign in again. */
  expire(): void;
  /** Demo only: false once the session was revoked or ran out (the HTTP API answers 401 instead). */
  sessionValid(): boolean;
  signOut(): Promise<void>;
  /** "Sign out of all devices": ends every session of this person on the server (this one too), then the sign-in screen. */
  signOutEverywhere(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** The auth state, or null outside an AuthProvider (components that also render standalone, e.g. LogForm in tests). */
export const useAuthMaybe = (): AuthState | null => useContext(AuthContext);

export function useAuth(): AuthState {
  const a = useContext(AuthContext);
  if (!a) throw new Error("useAuth outside AuthProvider");
  return a;
}

const KEY = "logwave.devUser";
interface Saved { email: string; name?: string }
const store = {
  get(): Saved | null {
    for (const s of [() => sessionStorage, () => localStorage]) {
      try {
        const raw = s().getItem(KEY);
        if (!raw) continue;
        return raw.startsWith("{") ? (JSON.parse(raw) as Saved) : { email: raw };
      } catch { /* blocked storage */ }
    }
    return null;
  },
  set(v: Saved | null) {
    for (const s of [() => sessionStorage, () => localStorage]) {
      try { if (v) s().setItem(KEY, JSON.stringify(v)); else s().removeItem(KEY); } catch { /* ignore */ }
    }
  },
};

/** The session token and who it belongs to (docs/AUTH.md: token in localStorage). */
export const SESSION_KEY = "cleancleaver.session";
export const sessionStore = {
  get(): SessionGrant | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw) as Partial<SessionGrant>;
      return typeof v.token === "string" && v.user && typeof v.user.email === "string" ? (v as SessionGrant) : null;
    } catch { return null; }
  },
  set(v: SessionGrant | null) {
    try { if (v) localStorage.setItem(SESSION_KEY, JSON.stringify(v)); else localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  },
};

export const nameFromEmail = (email: string) => email.split("@")[0]!.replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const toUser = (s: Saved): AuthUser => ({ uid: `dev:${s.email}`, email: s.email, name: s.name || nameFromEmail(s.email) });
const fromGrant = (g: SessionGrant): AuthUser => ({ uid: g.user.id, email: g.user.email, name: g.user.name || nameFromEmail(g.user.email) });

export function defaultAuthClient(): AuthClient {
  return IS_MOCK ? createMockAuth({ store: sharedMockStore() }) : createHttpAuth(config.apiUrl);
}

/** Test helper: a fixed signed-in user with dev headers. `name`: a sign-in that carries a name (Google), else none (an email code). */
export function StaticAuthProvider({ email, name, children, onSignOut, onSignInDev }: { email: string; name?: string; children: ReactNode; onSignOut?: () => void; /** Tests: who a "pick who you are" or the demo's switch signed in as. */ onSignInDev?: (email: string, name?: string) => void }) {
  const value = useMemo<AuthState>(() => ({
    mode: "dev", status: "signedIn", kind: "dev", user: toUser({ email, ...(name ? { name } : {}) }), client: createHttpAuth(config.apiUrl),
    headers: async () => ({ "x-dev-user": email }), signInDev: (e: string, n?: string) => { onSignInDev?.(e, n); }, signInWithSession: () => {}, expire: () => {},
    sessionValid: () => true, signOut: async () => { onSignOut?.(); }, signOutEverywhere: async () => {},
  }), [email, name, onSignOut, onSignInDev]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

type Who = { kind: "dev"; user: AuthUser } | { kind: "session"; user: AuthUser; token: string };

export function AuthProvider({ children, mode = config.authMode, client: given }: { children: ReactNode; mode?: AuthMode; client?: AuthClient }) {
  const client = useMemo(() => given ?? defaultAuthClient(), [given]);
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [who, setWho] = useState<Who | undefined>();
  const [notice, setNotice] = useState<string>();
  // The in-memory identity, not storage: another tab signing in as someone else must not change who this tab is.
  const current = useRef<Who | undefined>(undefined);

  const become = useCallback((w: Who | undefined, msg?: string) => {
    current.current = w;
    setWho(w);
    setNotice(msg);
    setStatus(w ? "signedIn" : "signedOut");
  }, []);

  useEffect(() => {
    const s = sessionStore.get();
    if (s) {
      if (client.check && !client.check(s.token)) { sessionStore.set(null); become(undefined, AUTH_MESSAGES.signed_out); return; }
      become({ kind: "session", user: fromGrant(s), token: s.token });
      return;
    }
    const saved = mode === "dev" ? store.get() : null;
    become(saved ? { kind: "dev", user: toUser(saved) } : undefined);
  }, [client, mode, become]);

  const headers = useCallback(async (): Promise<Record<string, string>> => {
    const w = current.current;
    if (!w) return {};
    return w.kind === "session" ? { Authorization: `Bearer ${w.token}` } : { "x-dev-user": w.user.email };
  }, []);

  const signInDev = useCallback((email: string, name?: string) => {
    const saved = { email: email.trim().toLowerCase(), ...(name ? { name } : {}) };
    store.set(saved);
    become({ kind: "dev", user: toUser(saved) });
  }, [become]);

  const signInWithSession = useCallback((g: SessionGrant) => {
    sessionStore.set(g);
    become({ kind: "session", user: fromGrant(g), token: g.token });
  }, [become]);

  const expire = useCallback(() => {
    if (current.current?.kind !== "session") return; // a dev identity refused by the API is shown as an error, not a sign-out
    sessionStore.set(null);
    become(undefined, AUTH_MESSAGES.signed_out);
  }, [become]);

  const sessionValid = useCallback(() => {
    const w = current.current;
    return w?.kind !== "session" || !client.check || client.check(w.token);
  }, [client]);

  const signOut = useCallback(async () => {
    const w = current.current;
    // Signing out on purpose forgets this person's unsent form drafts (a 401 keeps them for after sign-in: lib/draft.ts).
    if (w) clearDrafts(w.user.email);
    // …and their room walk photos not sent yet (lib/walkUploads.ts; a 401 keeps them too).
    if (w) void clearWalkPhotosOf(w.user.email);
    if (w?.kind === "session") {
      try { await client.logout(w.token); } catch { /* already gone on the server: still sign out here */ }
      sessionStore.set(null);
    } else store.set(null);
    become(undefined);
  }, [client, become]);

  // Unlike signOut, a failure here is shown: the person asked for every device, so "done" must be true.
  const signOutEverywhere = useCallback(async () => {
    const w = current.current;
    if (w?.kind !== "session") return signOut();
    await client.logoutAll(w.token);
    clearDrafts(w.user.email);
    void clearWalkPhotosOf(w.user.email);
    sessionStore.set(null);
    become(undefined, "You're signed out on every device.");
  }, [client, become, signOut]);

  const value = useMemo<AuthState>(() => ({
    mode, status, kind: who?.kind, user: who?.user, notice, client,
    headers, signInDev, signInWithSession, expire, sessionValid, signOut, signOutEverywhere,
  }), [mode, status, who, notice, client, headers, signInDev, signInWithSession, expire, sessionValid, signOut, signOutEverywhere]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
